import { useState, useEffect, useRef, useCallback } from 'react'
import Keycloak from 'keycloak-js'
import { createContainer } from 'unstated-next'
import request from 'request-promise'
import Form from './Form'
import Student from './Student'
import LangAccessCodes from './LangAccessCodes'
import { navigate } from '@reach/router'
import { isEmpty } from 'ramda'
import { fetchAccountsStatuses } from './lib/accounts'
const { REACT_APP_API_URI, REACT_APP_KEYCLOAK_CONFIG, REACT_APP_PUBLIC_URI } =
    process.env

export function useAuth() {
    const [auth, setAuth] = useState({ authenticated: undefined })
    const keycloakRef = useRef(null)
    const { setForm } = Form.useContainer()
    const { setStudent } = Student.useContainer()
    const { setLangAccessCodes } = LangAccessCodes.useContainer()

    useEffect(() => {
        const keycloak = new Keycloak(REACT_APP_KEYCLOAK_CONFIG)
        keycloakRef.current = keycloak

        keycloak.onAuthRefreshSuccess = () => {
            setAuth((prev) => ({ ...prev, token: keycloak.token }))
        }
        keycloak.onTokenExpired = () => {
            keycloak.updateToken(30).catch(() => keycloak.login())
        }

        keycloak
            .init({
                checkLoginIframe: false,
                pkceMethod: 'S256',
                useNonce: false, // workaround for keycloak-js v21 bug that
                                 // checks nonce on access/refresh tokens too
            })
            .then((authenticated) => {
                if (!authenticated) {
                    setAuth({ authenticated: false })
                    return
                }
                return keycloak.loadUserProfile().then((profile) => {
                    return request({
                        uri: `${REACT_APP_API_URI}/context`,
                        json: true,
                        headers: {
                            Authorization: `Bearer ${keycloak.token}`,
                        },
                    }).then(({ form, student, langAccessCodes }) => {
                        if (isEmpty(student)) {
                            navigate('/unauthorized')
                            return
                        }
                        if (student.canDebug) {
                            student.passwordHasBeenModified = true
                            student.eduIDIsLinked = true
                            student.entraMfa = true
                            student.mfaConfigured = true
                        }
                        const requestedPath =
                            sessionStorage.getItem('requestedPath')
                        sessionStorage.removeItem('requestedPath')
                        if (requestedPath) {
                            navigate(requestedPath)
                        } else if (
                            window.location.pathname.endsWith('/callback')
                        ) {
                            navigate('/2026/accueil')
                        }
                        setAuth({
                            user: profile.username,
                            token: keycloak.token,
                            authenticated,
                        })
                        setForm(form)
                        setStudent(student)
                        setLangAccessCodes(langAccessCodes)
                        if (!student.canDebug && student.guid) {
                            fetchAccountsStatuses(student.guid)
                                .then((data) => {
                                    if (data) {
                                        setStudent((prev) => ({ ...prev, ...data }))
                                    }
                                })
                                .catch((err) =>
                                    console.error('Fetching account statuses failed:', err)
                                )
                        }
                    })
                })
            })
            .catch((err) => {
                console.error('Keycloak init / context fetch failed:', err)
                if (window.location.hash) {
                    window.history.replaceState({}, '', window.location.pathname)
                }
                setAuth({ authenticated: false })
            })

        const refreshInterval = setInterval(() => {
            if (keycloakRef.current && keycloakRef.current.authenticated) {
                keycloakRef.current
                    .updateToken(30)
                    .catch(() => keycloakRef.current.login())
            }
        }, 10000)

        return () => clearInterval(refreshInterval)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const login = useCallback(() => {
        if (keycloakRef.current) {
            const { pathname } = window.location
            if (
                pathname.startsWith('/2026/') &&
                !pathname.startsWith('/2026/load/')
            ) {
                sessionStorage.setItem('requestedPath', pathname)
            }
            keycloakRef.current.login({
                redirectUri: `${REACT_APP_PUBLIC_URI}/callback`,
            })
        }
    }, [])

    const logout = useCallback(() => {
        if (keycloakRef.current) {
            keycloakRef.current.logout()
        }
    }, [])

    return { ...auth, login, logout }
}

export default createContainer(useAuth)
